import { GET_COMPANY } from "@apiUtils";
import { mutationFetch } from "@atlas/sdk";
import type {
  IApplicationGroup,
  IApplicationGroupsResponse,
  IGroupMappingItem,
  IGroupMappingsResponse,
} from "@models";
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

export const APPLICATION_GROUPS_KEY = "applicationGroupsKey";
export const GROUP_MAPPINGS_KEY = "groupMappingsKey";

export const getApplicationGroupsKey = (
  companyUuid: string,
  appMapId: string,
  basePath?: string,
) => [APPLICATION_GROUPS_KEY, companyUuid, appMapId, basePath] as const;

export const getGroupMappingsKey = (
  companyUuid: string,
  appMapId: string,
  basePath?: string,
) => [GROUP_MAPPINGS_KEY, companyUuid, appMapId, basePath] as const;

const buildCompanyUrl = (
  companyUuid: string,
  suffix: string,
  basePath?: string,
) => {
  const path = `${GET_COMPANY}/${companyUuid}${suffix}`;
  return basePath ? `${basePath.replace(/\/$/, "")}${path}` : path;
};

export const useApplicationGroups = (
  companyUuid: string | undefined,
  appMapId: string | undefined,
  basePath?: string,
  options?: Partial<
    UseQueryOptions<
      IApplicationGroup[],
      AxiosError,
      IApplicationGroup[],
      ReturnType<typeof getApplicationGroupsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getApplicationGroupsKey(
      companyUuid ?? "",
      appMapId ?? "",
      basePath,
    ),
    queryFn: async () => {
      const url = buildCompanyUrl(
        companyUuid ?? "",
        `/app-map/${encodeURIComponent(appMapId ?? "")}/application-groups`,
        basePath,
      );
      const data = await mutationFetch<IApplicationGroupsResponse>({
        url,
        method: "GET",
      });
      return Array.isArray(data.groups) ? data.groups : [];
    },
    enabled: !!companyUuid && !!appMapId,
    ...options,
  });
};

export const useGroupMappings = (
  companyUuid: string | undefined,
  appMapId: string | undefined,
  basePath?: string,
  options?: Partial<
    UseQueryOptions<
      IGroupMappingItem[],
      AxiosError,
      IGroupMappingItem[],
      ReturnType<typeof getGroupMappingsKey>
    >
  >,
) => {
  return useQuery({
    queryKey: getGroupMappingsKey(companyUuid ?? "", appMapId ?? "", basePath),
    queryFn: async () => {
      const url = buildCompanyUrl(
        companyUuid ?? "",
        `/app-map/${encodeURIComponent(appMapId ?? "")}/group-mappings`,
        basePath,
      );
      try {
        const data = await mutationFetch<IGroupMappingsResponse>({
          url,
          method: "GET",
        });
        return Array.isArray(data.mappings) ? data.mappings : [];
      } catch (error) {
        const status = (error as AxiosError)?.response?.status;
        if (status === 404) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!companyUuid && !!appMapId,
    ...options,
  });
};

export type ISaveGroupMappingsPayload = {
  mappings: IGroupMappingItem[];
};

const invalidateGroupMappingQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  companyUuid: string,
  appMapId: string,
) => {
  queryClient.invalidateQueries({
    queryKey: [GROUP_MAPPINGS_KEY, companyUuid, appMapId],
  });
};

/** Replaces the full mapping set (OpenAPI PUT replaceApplicationGroupMappings). */
export const useSaveGroupMappings = (
  companyUuid: string | undefined,
  appMapId: string | undefined,
  basePath?: string,
  options?: UseMutationOptions<
    IGroupMappingItem[],
    AxiosError,
    ISaveGroupMappingsPayload
  >,
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};
  return useMutation({
    ...mutationOptions,
    mutationFn: async ({ mappings }) => {
      const url = buildCompanyUrl(
        companyUuid ?? "",
        `/app-map/${encodeURIComponent(appMapId ?? "")}/group-mappings`,
        basePath,
      );
      const data = await mutationFetch<IGroupMappingsResponse>({
        url,
        method: "PUT",
        body: { mappings },
      });
      return Array.isArray(data.mappings) ? data.mappings : mappings;
    },
    onSuccess: (...args) => {
      if (companyUuid && appMapId) {
        invalidateGroupMappingQueries(queryClient, companyUuid, appMapId);
      }
      onSuccess?.(...args);
    },
  });
};
